import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import axios from "axios";

import {
  REQUEST_SONG_UPLOAD,
  ANALYZE_USER_SONG,
  USER_SONG,
} from "../../../GraphQL/userSong";
import { validateAudioFile } from "../../../Utilities/audioValidation";

export type CustomSongStatus =
  | "idle"
  | "validating"
  | "uploading"
  | "analyzing"
  | "done"
  | "error";

export interface IUserSongTuning {
  chosenDropMs?: number | null;
  beatPhaseOffset?: number | null;
  leadBars?: number | null;
  mixWeight?: number | null;
}

export interface IUserSong {
  id: string;
  status: string;
  errorNote?: string | null;
  title?: string | null; // user-editable display name; server defaults it from the filename
  originalFilename?: string | null;
  durationMs?: number | null;
  tempoBpm?: number | null;
  tuning?: IUserSongTuning | null;
  previewUrl?: string | null; // short-lived presigned B2 GET — do not persist
  analysisUrl?: string | null; // short-lived presigned B2 GET — do not persist
}

const POLL_MS = 2000;

// Drives a custom song through: validate -> presigned PUT to B2 -> analyzeUserSong
// -> poll userSong until DONE/ERROR. Returns null `song` until analysis completes.
export const useCustomSongUpload = () => {
  const [status, setStatus] = useState<CustomSongStatus>("idle");
  const [progress, setProgress] = useState(0);
  const [songId, setSongId] = useState<string | null>(null);
  const [song, setSong] = useState<IUserSong | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [requestUpload] = useMutation(REQUEST_SONG_UPLOAD);
  const [analyze] = useMutation(ANALYZE_USER_SONG);

  // poll only while analyzing; the skip flips off as soon as we reach a terminal state
  const { data: pollData } = useQuery(USER_SONG, {
    variables: { songId },
    skip: !songId || status !== "analyzing",
    fetchPolicy: "network-only",
    pollInterval: POLL_MS,
  });

  // React to the polled result via `data` (not `onCompleted`, which Apollo does
  // not fire on pollInterval refetches — the loader would spin forever).
  useEffect(() => {
    const polled: IUserSong | null = pollData?.userSong ?? null;
    if (!polled) return;
    if (polled.status === "DONE") {
      setSong(polled);
      setStatus("done");
    } else if (polled.status === "ERROR") {
      // never surface raw backend errorNote text (older docs may carry
      // internal tool output) — show a generic, actionable message
      setError(
        "Analysis failed — make sure the file is a valid audio file (mp3, wav, m4a, ogg, flac) and try again.",
      );
      setStatus("error");
    }
  }, [pollData]);

  const reset = useCallback(() => {
    setStatus("idle");
    setProgress(0);
    setSongId(null);
    setSong(null);
    setError(null);
  }, []);

  const upload = useCallback(
    async (file: File) => {
      setError(null);
      setSong(null);
      setProgress(0);

      setStatus("validating");
      const validation = await validateAudioFile(file);
      if (!validation.ok) {
        setError(validation.reason || "Invalid file");
        setStatus("error");
        return;
      }

      // the PUT's Content-Type must match the one the presigned URL was signed with
      const contentType = file.type || "audio/mpeg";
      setStatus("uploading");
      try {
        const { data } = await requestUpload({
          variables: { filename: file.name, contentType },
        });
        const requested = data?.requestSongUpload;
        if (!requested?.uploadUrl || !requested?.songId)
          throw new Error("Upload could not be initialized");

        await axios.put(requested.uploadUrl, file, {
          headers: { "Content-Type": contentType },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
          },
        });

        setSongId(requested.songId);
        setStatus("analyzing");
        await analyze({ variables: { songId: requested.songId } });
        // the useQuery poll above takes over and drives status -> done/error
      } catch (e) {
        setError(e instanceof Error ? e.message : "Upload failed");
        setStatus("error");
      }
    },
    [requestUpload, analyze],
  );

  return { status, progress, song, songId, error, upload, reset };
};
